import React, {useState, useEffect, useCallback, useMemo} from "react";
import DetallePago from "./DetallePago";
import printA4 from "../../helpers/A4";
import downprintA4 from "../../helpers/DA4";
import "./styles/Pagos.css";
import {
    FACTURA,
    YAPE,
    BOLETA,
    VALE,
    round,
    EFECTIVO,
    MASTER,
    POS,
    AMERICAN,
    OTROS,
    PAGO_CREDITO,
    RAPPI,
    PLIN,
    decimalAdjust,
    CONTADO,
} from "../../Global";
import EfectivoImg from "./images/efectivo.png";
import Mastercard from "./images/mastercard.png";
import American from "./images/american.png";
import OtrosPagos from "./images/otros.png";
import Credito from "./images/credito.png";
import Cliente from "./images/cliente.png";
import Yape from "./images/yape.png";
import Plin from "./images/plin logo.png";
import Rappi from "./images/rappi logo.png";
import Pos from "./images/pos-icon.png";
import ReactTable from "react-table";
import "react-table/react-table.css";
import matchSorter from "match-sorter";
import {BotonGestionPreventas, BotonNuevaPreventa} from "../../componentes/Plantillas/Botones";
import { GuiaRemisionSelect } from "./GuiaRemisionSelect";
import Modal from "../../componentes/clientes/ClienteModal";
import { notificarMsg } from "../../componentes/Almacenes/AlmacenNotify";

const DEFAULT_VALIDATION_CODE = 'XXXXXXXX'

// Constantes centralizadas
const PAYMENT_METHODS_TEXT = {
    [YAPE]: "Yape",
    [POS]: "POS", 
    [OTROS]: "Otros",
    [PAGO_CREDITO]: "Crédito",
    [PLIN]: "Plin",
    [RAPPI]: "Rappi",
    [MASTER]: "Mastercard",
    [AMERICAN]: "American Express",
};

const EXCEED_BALANCE_ALLOWED_TYPES = [EFECTIVO];

// Configuración de métodos de pago para renderizado
const PAYMENT_METHODS_CONFIG = {
    [EFECTIVO]: {
        src: EfectivoImg,
        className: "btn-outline-success",
        title: "Efectivo",
        requiresValidation: false
    },
    [POS]: {
        src: Pos,
        className: "btn-outline-secondary",
        title: "POS",
        requiresValidation: false
    },
    [YAPE]: {
        src: Yape,
        className: "btn-yape",
        title: "Yape",
        requiresValidation: false
    },
    [MASTER]: {
        src: Mastercard,
        className: "btn-outline-danger",
        title: "Mastercard",
        requiresValidation: false
    },
    [AMERICAN]: {
        src: American,
        className: "btn-outline-info",
        title: "American Express",
        requiresValidation: false
    },
    [OTROS]: {
        src: OtrosPagos,
        className: "btn-outline-warning",
        title: "Otros métodos de pago",
        requiresValidation: true,
        permission: 'pago-otros'
    },
    [PAGO_CREDITO]: {
        src: Credito,
        className: "btn-outline-danger",
        title: "Crédito",
        requiresValidation: true,
        permission: 'vender-a-credito'
    },
    [PLIN]: {
        src: Plin,
        className: "btn-outline-info",
        title: "Plin",
        requiresValidation: false
    },
    [RAPPI]: {
        src: Rappi,
        className: "btn-outline-warning",
        title: "Rappi",
        requiresValidation: false
    }
};

const PagosUI = props => {
    // Estados
    const [showNuevaPreventa, setShowNuevaPreventa] = useState(false);
    const [showModalValidacion, setShowModalValidacion] = useState(false);
    const [modalValidacionTipoPago, setModalValidacionTipoPago] = useState(null);
    const [codigosValidacionUsuario, setCodigosValidacionUsuario] = useState([]);
    const [typedCodigoUsuario, setTypedCodigoUsuario] = useState("");
    const [confEmpresa, setConfEmpresa] = useState({ showExactChange: false });

    // Cálculos memoizados
    const saldoRestante = useMemo(() => {
        return round(props.Total - props.TotalPagado, 2);
    }, [props.Total, props.TotalPagado]);

    const vueltoCalculado = useMemo(() => {
        if (!props.Vuelto) return "0.00";
        
        if (!confEmpresa.showExactChange) {
            const vuelto = props.TotalPagado - props.TotalRedondeado;
            return vuelto ? vuelto.toLocaleString("en", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }) : "0.00";
        }
        
        return decimalAdjust("floor", props.Vuelto, -2);
    }, [props.Vuelto, props.TotalPagado, props.TotalRedondeado, confEmpresa]);

    const clienteVarios = useMemo(() => {
        return props.preventa.NroTipoDocumento === "99999999";
    }, [props.preventa.NroTipoDocumento]);

    const hasCreditPayment = useMemo(() => {
        return props.Pagos.some(p => p.IdTipoPago === PAGO_CREDITO);
    }, [props.Pagos]);

    const lastPaymentIsContado = useMemo(() => {
        return props.Pagos.length > 0 && props.Pagos[props.Pagos.length - 1].IdTipoPago === CONTADO;
    }, [props.Pagos]);

    const disableCredit = useMemo(() => {
        return clienteVarios || hasCreditPayment;
    }, [clienteVarios, hasCreditPayment]);

    const disableAll = useMemo(() => {
        if (props.Pagos.length === 0) return false;
        if (lastPaymentIsContado) return true;
        
        const isCompleted = saldoRestante <= 0;
        const hasExceedablePayment = props.Pagos.some(p => 
            EXCEED_BALANCE_ALLOWED_TYPES.includes(p.IdTipoPago)
        );
        
        return isCompleted && !hasExceedablePayment;
    }, [props.Pagos.length, lastPaymentIsContado, saldoRestante, props.Pagos]);

    const shouldRemovePayments = useMemo(() => {
        if (!Array.isArray(props.Pagos) || props.Pagos.length === 0) {
            return false;
        }

        const total = props.Total;
        let accumulated = 0;
        let hasNegativeBalance = false;
        
        for (let i = 0; i < props.Pagos.length; i++) {
            const pago = props.Pagos[i];
            accumulated += pago.Monto;
            
            if (accumulated > total && i < props.Pagos.length - 1) {
                if (!EXCEED_BALANCE_ALLOWED_TYPES.includes(pago.IdTipoPago)) {
                    hasNegativeBalance = true;
                    break;
                }
            }
        }
        
        return hasNegativeBalance;
    }, [props.Pagos, props.Total]);

    // Callbacks optimizados
    const fetchConfEmpresa = useCallback(async () => {
        try {
            const req = await fetch(`/api/config/emp`);
            const conf = await req.json();

            if (!('showExactChange' in conf)) {
                conf['showExactChange'] = false;
            }

            return conf;
        } catch (e) {
            console.error("Error fetching empresa config:", e);
            return { showExactChange: false };
        }
    }, []);

    const fetchCodigosValidacion = useCallback(async () => {
        try {
            const req = await fetch('/api/usuarios/codigoConfirmacion/sucursal');
            
            if (!req.ok) {
                throw new Error("Failed to fetch validation codes");
            }

            const res = await req.json();
            return res.map(c => c.Codigo);
        } catch (e) {
            console.error("Error fetching validation codes:", e);
            return [DEFAULT_VALIDATION_CODE];
        }
    }, []);

    const canPaymentTypeExceedBalance = useCallback((tipoPago) => {
        return EXCEED_BALANCE_ALLOWED_TYPES.includes(tipoPago);
    }, []);

    const validatePaymentAmount = useCallback((tipoPago) => {
        if (saldoRestante <= 0 && !canPaymentTypeExceedBalance(tipoPago)) {
            const tipoTexto = PAYMENT_METHODS_TEXT[tipoPago] || 'este tipo';
            notificarMsg(
                `No se puede agregar un pago de ${tipoTexto} porque el monto ya está cubierto y este método no permite vuelto.`,
                'warning'
            );
            return false;
        }
        return true;
    }, [saldoRestante, canPaymentTypeExceedBalance]);

    // Efectos
    useEffect(() => {
        const initializeData = async () => {
            try {
                const [codigos, empresaConf] = await Promise.all([
                    fetchCodigosValidacion(),
                    fetchConfEmpresa()
                ]);
                
                setCodigosValidacionUsuario(codigos);
                setConfEmpresa(empresaConf);
            } catch (error) {
                console.error("Error initializing data:", error);
            }
        };

        initializeData();
    }, [fetchCodigosValidacion, fetchConfEmpresa]);

    // Handlers
    const handlePaymentMethodClick = useCallback((tipoPago) => {
        const methodConfig = PAYMENT_METHODS_CONFIG[tipoPago];
        
        if (!validatePaymentAmount(tipoPago)) {
            return;
        }

        if (methodConfig.requiresValidation) {
            checkPerm(methodConfig.permission, tipoPago);
        } else {
            props.handleTipoPago(tipoPago)();
        }
    }, [validatePaymentAmount, props.handleTipoPago]);

    const checkPerm = useCallback(async (perm, tipoPago) => {
        try {
            const req = await fetch(`/api/usuarios/${perm}/validar`);
            
            if (!req.ok) {
                throw new Error("Permission validation failed");
            }

            const data = await req.json();
            setModalValidacionTipoPago(tipoPago);
            setShowModalValidacion(data.codigoConfirmacion);
        } catch (e) {
            console.error("Permission check error:", e);
            props.handleTipoPago(tipoPago)();
            setShowModalValidacion(false);
        }
    }, [props.handleTipoPago]);

    const validarCodigoUsuario = useCallback(() => {
        const validCode = codigosValidacionUsuario.some(c => c === typedCodigoUsuario);
        
        if (validCode) {
            props.handleTipoPago(modalValidacionTipoPago)();
            setShowModalValidacion(false);
            notificarMsg("Validación completada exitosamente.");
        } else {
            notificarMsg('El código ingresado no es correcto.', 'warning');
        }
        setTypedCodigoUsuario("");
    }, [codigosValidacionUsuario, typedCodigoUsuario, modalValidacionTipoPago, props.handleTipoPago]);

    const validateMontoUpdate = useCallback(async (index, nuevoMonto, tipoPago) => {
        const montoNumerico = parseFloat(nuevoMonto) || 0;
        
        if (!canPaymentTypeExceedBalance(tipoPago)) {
            const montoActual = props.Pagos[index]?.Monto || 0;
            const saldoAjustado = saldoRestante + montoActual;
            
            if (montoNumerico > saldoAjustado) {
                const tipoTexto = PAYMENT_METHODS_TEXT[tipoPago] || 'este tipo de pago';
                notificarMsg(
                    `El monto de ${tipoTexto} no puede ser mayor a S/. ${saldoAjustado.toFixed(2)} porque este método no permite vuelto.`,
                    'warning'
                );
                
                await props.updateMontoPago(index, Math.max(0, saldoAjustado).toFixed(2));
                return;
            }
        }
        
        await props.updateMontoPago(index, nuevoMonto);
    }, [canPaymentTypeExceedBalance, saldoRestante, props.Pagos, props.updateMontoPago]);

    const handleFirstButtonClick = useCallback(() => {
        if (shouldRemovePayments) {
            alert("El orden de los medios de pago seleccionados no es válido. Verifique si todos son necesarios e inténtelo otra vez.");
            return;
        }
        setShowNuevaPreventa(true);
        props.handlePagar();
    }, [shouldRemovePayments, props.handlePagar]);

    // Render helpers
    const renderPaymentMethodsCarouselDesktop = () => {
        // Definir los grupos de métodos para el carousel desktop
        const paymentGroups = [
            // Grupo 1: Métodos principales
            [POS, EFECTIVO, YAPE],
            // Grupo 2: Tarjetas y otros
            [MASTER, AMERICAN, OTROS],
            // Grupo 3: Crédito y digitales
            [PAGO_CREDITO, PLIN, RAPPI].filter(method => 
                PAYMENT_METHODS_CONFIG[method]
            )
        ].filter(group => group.length > 0);

        return (
            <div id="carousel-menu-method-pagos" className="carousel carousel-dark slide w-100 botones-desk" data-interval="false">
                <div className="carousel-inner">
                    {paymentGroups.map((group, groupIndex) => (
                        <div key={`group-${groupIndex}`} className={`carousel-item ${groupIndex === 0 ? 'active' : ''}`}>
                            <div className="pagos__methods--container">
                                {group.map((method) => {
                                    const config = PAYMENT_METHODS_CONFIG[method];
                                    if (!config) return null;
                                    
                                    const isDisabled = method === PAGO_CREDITO 
                                        ? disableAll || disableCredit
                                        : disableAll || props.creditoSelected;

                                    const additionalDisabled = method === EFECTIVO 
                                        ? props.PagoEfectivoUnico 
                                        : false;

                                    return (
                                        <div key={method} className="pagos__methods ml-2">
                                            <input
                                                type="image"
                                                src={config.src}
                                                className={`btn ${config.className}`}
                                                onClick={() => handlePaymentMethodClick(method)}
                                                data-toggle="tooltip"
                                                data-placement="bottom"
                                                title={config.title}
                                                disabled={isDisabled || additionalDisabled}
                                                tabIndex="1"
                                                {...(method === EFECTIVO && { autoFocus: true })}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
                {paymentGroups.length > 1 && (
                    <>
                        <a className="carousel-control-prev btn-atras" href="#carousel-menu-method-pagos" role="button" data-slide="prev">
                            <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                            <span className="sr-only">Previous</span>
                        </a>
                        <a className="carousel-control-next" href="#carousel-menu-method-pagos" role="button" data-slide="next">
                            <span className="carousel-control-next-icon" aria-hidden="true"></span>
                            <span className="sr-only">Next</span>
                        </a>
                    </>
                )}
            </div>
        );
    };

    const renderPaymentMethodsCarouselMobile = () => {
        const allMethods = [
            POS, EFECTIVO, YAPE, MASTER, AMERICAN, OTROS, PAGO_CREDITO, 
            PLIN, RAPPI
        ].filter(method => PAYMENT_METHODS_CONFIG[method]);

        return (
            <div id="carousel-menu-method-pagos-2" className="carousel carousel-dark slide w-100 botones-resp" data-interval="false">
                <div className="carousel-inner">
                    {allMethods.map((method, index) => {
                        const config = PAYMENT_METHODS_CONFIG[method];
                        if (!config) return null;
                        
                        const isDisabled = method === PAGO_CREDITO 
                            ? disableAll || disableCredit
                            : disableAll || props.creditoSelected;

                        const additionalDisabled = method === EFECTIVO 
                            ? props.PagoEfectivoUnico 
                            : false;

                        return (
                            <div key={method} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
                                <div className="pagos__methods--container">
                                    <div className="pagos__methods">
                                        <input
                                            type="image"
                                            src={config.src}
                                            className={`btn ${config.className}`}
                                            onClick={() => handlePaymentMethodClick(method)}
                                            data-toggle="tooltip"
                                            data-placement="bottom"
                                            title={config.title}
                                            disabled={isDisabled || additionalDisabled}
                                            tabIndex="1"
                                            {...(method === EFECTIVO && { autoFocus: true })}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {allMethods.length > 1 && (
                    <>
                        <a className="carousel-control-prev btn-atras" href="#carousel-menu-method-pagos-2" role="button" data-slide="prev">
                            <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                            <span className="sr-only">Previous</span>
                        </a>
                        <a className="carousel-control-next" href="#carousel-menu-method-pagos-2" role="button" data-slide="next">
                            <span className="carousel-control-next-icon" aria-hidden="true"></span>
                            <span className="sr-only">Next</span>
                        </a>
                    </>
                )}
            </div>
        );
    };

    const renderTotalItems = () => {
        const items = [
            { key: 'gravado', value: props.TotalGravadas, label: 'Gravado' },
            { key: 'inafecto', value: props.TotalInafectas, label: 'Inafecto' },
            { key: 'exonerado', value: props.TotalExoneradas, label: 'Exonerado' },
            { key: 'gratuito', value: props.Gratuitas, label: 'Gratuito' },
            { key: 'icbper', value: props.TotalICBPER, label: 'ICBPER' },
            { key: 'igv', value: props.TotalIGV, label: 'IGV' },
            { key: 'redondeo', value: props.redondeo, label: 'Redondeo' }
        ];

        return items.map(item => {
            if (item.value > 0) {
                return (
                    <p key={item.key} value={item.value}>
                        <strong>
                            <span>
                                <span>{item.label}</span><br/>
                                <span style={{whiteSpace: 'nowrap'}}>
                                    S/. {decimalAdjust('floor', item.value, -2)}
                                </span>
                            </span>
                        </strong>
                    </p>
                );
            }
            return null;
        });
    };

    return (
        <>
            <div className="pagos__container" tabIndex="-1">
                <div className="row pagos">
                    <div className="preventa__container_data col-12 col-sm-6">
                        <div className="row">
                            <div className="col-12 col-sm-6 mt-3">
                                {showNuevaPreventa && (
                                    <BotonGestionPreventas
                                        to="/gestionpreventas"
                                        title="Lista de Preventas Pendientes"
                                        tabIndex="0"
                                    />
                                )}
                                {!props.isLoading && !props.hasPayed && props.permisosEditar && (
                                    <button
                                        type="button"
                                        className="boton__naranja ml-1"
                                        title="Editar Preventa"
                                        onClick={() => props.setRedirect("/preVentas/edit/" + props.IdPreventa)}
                                    >
                                        <i className="fa fa-edit" />
                                    </button>
                                )}
                            </div>
                            <div className="col-12 col-sm-6 d-flex justify-content-end preventa__cliente mt-3">
                                <input
                                    type="image"
                                    src={Cliente}
                                    data-toggle="tooltip"
                                    title="Datos del Cliente"
                                    width="40px"
                                    height="50px"
                                    tabIndex="0"
                                />
                                <div className="preventa__cliente--contenido">
                                    <p>{props.preventa.Alias}</p>
                                    <p><strong>{props.preventa.NroTipoDocumento}</strong></p>
                                    <p>{props.preventa.RazonSocial}</p>
                                </div>
                            </div>
                        </div>
                        <div className="preventa__carrito">
                            <ReactTable
                                tabIndex="0"
                                id="table-productos-to-xls"
                                data={props.detallePreventa}
                                filterable
                                defaultFilterMethod={(filter, row) => String(row[filter.id]) === filter.value}
                                columns={[
                                    {
                                        columns: [
                                            {
                                                Header: "Descripción",
                                                id: "NombreProductoConPresentacion",
                                                accessor: d => {
                                                    let nombreProducto = d.NombreProductoConPresentacion;
                                                    if (nombreProducto.includes(d.unidadMedida)) {
                                                        nombreProducto = nombreProducto.replace(d.unidadMedida, '') + ' ' + d.unidadMedida;
                                                    } else {
                                                        nombreProducto = nombreProducto + ' ' + d.unidadMedida;
                                                    }
                                                    if (nombreProducto.includes(d.NombrePresentacion)) {
                                                        nombreProducto = nombreProducto.replace(d.NombrePresentacion, '');
                                                    }
                                                    return nombreProducto;
                                                },
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, {
                                                        keys: ["NombreProductoConPresentacion"]
                                                    }),
                                                filterAll: true
                                            },
                                            {
                                                Header: "Cantidad",
                                                id: "Cantidad",
                                                accessor: d => d.Cantidad,
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, { keys: ["Cantidad"] }),
                                                filterAll: true,
                                                style: { textAlign: "right" }
                                            },
                                            {
                                                Header: "Precio",
                                                id: "PrecioVenta",
                                                accessor: d => `S/. ${decimalAdjust('floor', d.PrecioVenta, -2)}`,
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, { keys: ["PrecioVenta"] }),
                                                filterAll: true,
                                                style: { textAlign: "right" }
                                            },
                                            {
                                                Header: "Descuento",
                                                id: "Descuento",
                                                accessor: d => `S/. ${decimalAdjust('floor', d.Descuento, -2)}`,
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, { keys: ["Descuento"] }),
                                                filterAll: true
                                            },
                                            {
                                                Header: "Total",
                                                id: "Total",
                                                accessor: d => `S/. ${decimalAdjust('floor', d.Total, -2)}`,
                                                filterMethod: (filter, rows) =>
                                                    matchSorter(rows, filter.value, { keys: ["Total"] }),
                                                filterAll: true
                                            }
                                        ]
                                    }
                                ]}
                                defaultPageSize={8}
                                className="-striped -highlight"
                                previousText="Anterior"
                                nextText="Siguiente"
                                noDataText="No se encontraron registros"
                                loadingText="Cargando..."
                                ofText="de"
                                rowsText="filas"
                                pageText="Página"
                                pageSizeOptions={[8, 20, 25, 50, 100]}
                            />
                        </div>
                    </div>

                    <div className="encabezado col-12 col-sm-6 encabezado-resp">
                        <div className="mt-1">PAGOS - {props.TipoModalidadPago}</div>
                        <div className="pagos__tipo--comprobante">
                            {[FACTURA, BOLETA, VALE].map((tipo, idx) => (
                                <button
                                    key={tipo}
                                    type="button"
                                    tabIndex="0"
                                    className={
                                        props.TipoComprobante === tipo
                                            ? "pagos__tipo--comprobante--item--active"
                                            : "pagos__tipo--comprobante--item"
                                    }
                                    hidden={props.TipoComprobante !== tipo}
                                    onClick={() => props.handleTipoComprobante(
                                        tipo === FACTURA ? 2 : tipo === BOLETA ? 4 : 1
                                    )}
                                >
                                    {tipo === FACTURA ? "Factura" : tipo === BOLETA ? "Boleta" : "Vale"}
                                </button>
                            ))}
                        </div>
                        <div className="detalles__pago">
                            <div className="detalles__pago__item">
                                {props.Pagos.map((pago, index) => (
                                    <DetallePago
                                        key={`pay_${index}`}
                                        IdTipoPago={pago.IdTipoPago}
                                        Monto={pago.Monto}
                                        Referencia={pago.Referencia}
                                        handleFocus={props.handleFocus}
                                        handleMontoPago={async (event) => {
                                            await validateMontoUpdate(index, event.target.value, pago.IdTipoPago);
                                        }}
                                        handleReferenciaPago={(event) => {
                                            props.updateReferenciaPago(index, event.target.value);
                                        }}
                                        handleSelectPlazosCredito={async (event) => {
                                            await props.updateFechaPago(index, event.target.value);
                                        }}
                                        PlazoCredito={pago.PlazoCredito}
                                        handlePagoEnterKeyUp={props.handlePagoEnterKeyUp}
                                        handleRemovePago={() => props.removePago(index)}
                                        fechaPago={pago.FechaPago}
                                    />
                                ))}
                            </div>
                        </div>
                        
                        <div className="pagos__methods--container w-100">
                            {renderPaymentMethodsCarouselDesktop()}
                            {renderPaymentMethodsCarouselMobile()}
                        </div>

                        <div className="info__input__container">
                            <div className="input__container">
                                <label>Vuelto</label>
                                <input
                                    style={{overflow: "hidden"}}
                                    className="input"
                                    tabIndex="0"
                                    aria-label="Soles"
                                    placeholder="0.00"
                                    readOnly
                                    value={vueltoCalculado}
                                />
                            </div>
                            <div className="input__container">
                                <label>Observaciones</label>
                                <input
                                    style={{overflow: "hidden"}}
                                    className="input"
                                    aria-label="Observaciones"
                                    placeholder=""
                                    value={props.Observaciones}
                                    onChange={props.handleObservaciones}
                                    tabIndex="0"
                                />
                            </div>
                            <div className="input__container d-none d-lg-block">
                                <label>Orden Compra</label>
                                <input
                                    style={{overflow: "hidden"}}
                                    className="input"
                                    aria-label="Observaciones"
                                    placeholder=""
                                    tabIndex="0"
                                    value={props.OrdenCompra}
                                    onChange={props.handleOrdenCompra}
                                />
                            </div>
                            <div className="d-none d-lg-block">
                                <GuiaRemisionSelect onChange={props.handleGuiaRemision} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="totals__container row">
                    <div className="totals__container--item col-12 col-sm-6">
                        {renderTotalItems()}
                    </div>
                    
                    <div className="totals__container--item-total col-12 col-sm-2 total-resp">
                        <span style={{display: "block", margin: "auto"}}>
                            {"S/" + props.Total.toLocaleString("en", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                            })}
                        </span>
                    </div>

                    <div className="totals__container--item col-12 col-sm-4 no-pad d-flex justify-content-between">
                        <p style={{margin: "0 0 0 1rem"}}>
                            <strong>
                                {saldoRestante > 0 && 
                                    `Pendiente S/${saldoRestante.toLocaleString("en", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                    })}`
                                }
                            </strong>
                        </p>

                        {props.isLoading ? "Cargando" : props.error ? "Ha ocurrido un error" : (
                            props.hasPayed ? (
                                <BotonNuevaPreventa
                                    to="/pre-invoice/add"
                                    title="Lista de Preventas Pendientes"
                                    tabIndex="0"
                                    style={{height: "100%"}}
                                />
                            ) : (
                                <button
                                    className="pagos__boton"
                                    onClick={handleFirstButtonClick}
                                    tabIndex="3"
                                    hidden={!props.detallePreventa.length}
                                    disabled={!(props.TotalPagado >= props.Total && props.ClickPagar)}
                                >
                                    PAGAR
                                </button>
                            )
                        )}

                        {props.Venta && props.hasPayed && (
                            <>
                                <div className="botones-desk">
                                    <button
                                        className="boton__verdeClaro--letras ml-1 ml-5"
                                        onClick={() => props.printTicketVenta(props.Venta, props.DetallesVenta)}
                                        tabIndex="4"
                                        style={{height: "100%", borderRadius: "15px"}}
                                    >
                                        Ticket <i className="fas fa-print"/>
                                    </button>
                                    <button
                                        onClick={() => printA4(props.DetallesVenta, props.Venta, props.extImg, props.cuentasBancarias)}
                                        className="boton__verdeOscuro--letras ml-1"
                                        tabIndex="5"
                                        style={{height: "100%", borderRadius: "15px"}}
                                    >
                                        A4 <i className="fas fa-print"/>
                                    </button>
                                </div>
                                <div className="botones-resp">
                                    <button
                                        onClick={() => downprintA4(props.DetallesVenta, props.Venta, props.extImg)}
                                        className="boton__verdeOscuro--letras ml-1"
                                        tabIndex="5"
                                    >
                                        A4 <i className="fas fa-print"/>
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Modal 
                isOpen={showModalValidacion} 
                onClose={() => setShowModalValidacion(false)} 
                width='900px'
                title="Validar venta crédito"
            >
                <label className="font-weight-bold" htmlFor="inputValidacion">
                    Código
                </label>
                <input 
                    type="password"
                    name="inputValidacion"
                    className="form-control mb-2"
                    value={typedCodigoUsuario}
                    onChange={e => setTypedCodigoUsuario(e.target.value)}
                />

                <div className="d-flex justify-content-end">
                    <button
                        className="btn btn-verde-mfp-letra-normal"
                        title="Validar"
                        onClick={validarCodigoUsuario}
                    >
                        Validar
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default PagosUI;